import type { Capability } from "@remi/enums/capability";
import { type OrganizationType, OrganizationTypeValues } from "@remi/enums/organization-type";
import { isOneOf } from "@remi/ts-utils";
import { REMI_INTERNAL_ORG } from "@remi/types/organizations";

import { hasCapability, type User } from "../base";

export const organization = {
  read: (user: User, organizationId: string) => {
    if (hasCapability(user, "organization.read")) {
      return true;
    }
    return user.person.organizationId === organizationId;
  },

  list: (user: User) => hasCapability(user, "organization.list"),

  listByType: (user: User, type: OrganizationType) => isOneOf(type, readableOrganizationTypes(user)),

  // The same two clauses `organizationWhere` applies, as data rather than a Prisma filter, for callers
  // that must read an organization row and then decide per-row whether its name may be shown. A null
  // `readableIds` means every id is readable; an empty `readableTypes` means none are.
  readScope: (user: User): { readableTypes: OrganizationType[]; readableIds: string[] | null } => ({
    readableTypes: readableOrganizationTypes(user),
    readableIds: hasCapability(user, "organization.read.all")
      ? null
      : [REMI_INTERNAL_ORG.id, user.person.organizationId],
  }),
};

export const organizationSettings = {
  update: (user: User) => hasCapability(user, "organizationSettings.update"),
};

const OrganizationTypeReadCapability = {
  CONTRACTOR: "organization.readType.contractor",
  FUNDING: "organization.readType.funding",
  HOMEOWNER: "organization.readType.homeowner",
  REMI_INTERNAL: "organization.readType.remiInternal",
  SOLAR_COMPANY: "organization.readType.solarCompany",
  VENDOR: "organization.readType.vendor",
} as const satisfies Record<OrganizationType, Capability>;

export const readableOrganizationTypes: (user: User) => OrganizationType[] = (user: User) =>
  OrganizationTypeValues.filter((type) => hasCapability(user, OrganizationTypeReadCapability[type]));

interface OrganizationScope {
  id: { in: string[] } | undefined;
  type: { in: OrganizationType[] };
}

export const organizationWhere: (input: { user: User; exclude?: OrganizationType[] }) => OrganizationScope = ({
  user,
  exclude,
}) => {
  // Flat return shape preserved — some callers in organizationDao spread
  // this and override `type`; rewriting to OR(clauses) would silently
  // break that composition. BONES-577 will unify this with a shared
  // scope structure that handles the type overlay correctly.
  return {
    id: hasCapability(user, "organization.read.all")
      ? undefined
      : { in: [REMI_INTERNAL_ORG.id, user.person.organizationId] },
    type: {
      in: readableOrganizationTypes(user).filter((type) => (exclude ? !exclude.includes(type) : true)),
    },
  };
};
